// i18n singleton for the buyer + admin SPAs.
//
// This module initializes ONE i18next instance as a side effect of import and
// registers it as react-i18next's global instance (via initReactI18next), so
// any component in any app that calls `useTranslation()` resolves against it
// without needing an <I18nextProvider> in the tree. Import order matters only
// in that this module must be imported before the first render that reads a
// translation — each app does that from its `main.tsx` (web) / entry (admin).
//
// Ownership of copy:
//   - `shared` namespace  → strings rendered by @recruiter/shared components
//     (loaded here, below).
//   - per-app namespaces  → each app registers its own resource bundles into
//     THIS instance via `registerNamespace()` (see web/src/i18n.ts). Keeping app
//     copy in the app package (not here) preserves the package boundary: shared
//     never imports an app.
//
// English is the only shipped locale today; the structure (locale-keyed
// resources, `fallbackLng`) lets a second locale drop in without touching call
// sites.

import i18next, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";

import enShared from "./locales/en/shared.json";

/** The single shared i18next instance. Apps register their namespaces onto it. */
export const i18n = i18next;

// Initialize exactly once. `createInstance`-free (default singleton) so that
// react-i18next's `useTranslation`/`Trans` pick it up globally with no provider.
if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    lng: "en",
    fallbackLng: "en",
    // `shared` is the default namespace; apps add their own and name them at
    // the call site: `useTranslation("stages")`.
    defaultNS: "shared",
    ns: ["shared"],
    resources: { en: { shared: enShared } } as Resource,
    interpolation: {
      // React already escapes; double-escaping would corrupt copy like `&`.
      escapeValue: false,
    },
    // Return "" rather than `null` so leaves are always renderable strings
    // (keeps TS call sites string-typed).
    returnNull: false,
    // Keep i18next's default (true): a value that is intentionally an EMPTY
    // string (e.g. a split-copy trailing run with no text) must render as ""
    // — NOT be treated as missing and echo the raw key. `false` here caused
    // `matching.phaseCopy.refining.b` (value "") to render as its key.
    returnEmptyString: true,
  });
}

/**
 * Register (or extend) a locale namespace on the shared instance. Apps call this
 * once at startup for each namespace they own. Deep-merges and overwrites so a
 * re-register (HMR) refreshes the bundle.
 */
export function registerNamespace(
  namespace: string,
  bundle: Record<string, unknown>,
  locale = "en",
): void {
  i18n.addResourceBundle(locale, namespace, bundle, /* deep */ true, /* overwrite */ true);
}

export { useTranslation, Trans } from "react-i18next";
export type { TFunction } from "i18next";
